import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiArrowLeft } from 'react-icons/fi';
import {
  listPredefinedCharacters,
  getPredefinedCharacter,
  createPredefinedCharacter,
  updatePredefinedCharacter,
  deletePredefinedCharacter
} from '../services/characterService.js';
import AdminCharacterForm from '../components/AdminCharacterForm';
import AdminCharacterList from '../components/AdminCharacterList';
import Spinner from '../components/Spinner';

const AdminCharacterManagement = () => {
  const { firebaseUser, userInfo } = useAuth();
  const navigate = useNavigate();

  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Get ID token from Firebase user
  const getIdToken = async () => {
    if (!firebaseUser) return null;
    return await firebaseUser.getIdToken();
  };

  // Fetch predefined characters
  const fetchCharacters = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      console.log('[FetchCharacters] Starting fetch...');
      const data = await listPredefinedCharacters(token, { limit: 50 });
      console.log('[FetchCharacters] Raw response:', data);
      console.log('[FetchCharacters] Is array?', Array.isArray(data));
      console.log('[FetchCharacters] data.characters?', data.characters);
      
      // Handle both array and object responses
      const characters = Array.isArray(data) ? data : (data.characters || []);
      console.log('[FetchCharacters] Setting characters:', characters);
      setCharacters(characters);
    } catch (err) {
      console.error('Error fetching characters:', err);
      setError(err.message || 'Failed to fetch characters');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (!firebaseUser) {
      navigate('/login');
      return;
    }
    fetchCharacters();
  }, [firebaseUser, navigate]);

  // Handle create new character
  const handleCreateNew = () => {
    setEditingCharacter(null);
    setShowForm(true);
  };

  // Handle edit character
  const handleEdit = async (character) => {
    try {
      const token = await getIdToken();
      const fullCharacter = await getPredefinedCharacter(token, character.characterId);
      setEditingCharacter(fullCharacter);
      setShowForm(true);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle form submit (create or update)
  const handleFormSubmit = async (formData) => {
    try {
      setFormLoading(true);
      setError(null);
      const token = await getIdToken();

      if (editingCharacter) {
        // Update existing
        await updatePredefinedCharacter(token, editingCharacter.characterId, formData);
      } else {
        // Create new
        await createPredefinedCharacter(token, formData);
      }

      setShowForm(false);
      setEditingCharacter(null);
      await fetchCharacters();
    } catch (err) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Handle delete character
  const handleDelete = async (characterId) => {
    try {
      setError(null);
      const token = await getIdToken();
      await deletePredefinedCharacter(token, characterId);
      setDeleteConfirm(null);
      await fetchCharacters();
    } catch (err) {
      setError(err.message);
    }
  };

  // Check if user is admin
  useEffect(() => {
    if (!userInfo?.role || userInfo.role !== 'admin') {
      navigate('/');
    }
  }, [userInfo, navigate]);

  if (!firebaseUser || loading) {
    return <Spinner />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center text-indigo-600 hover:text-indigo-800 mb-4"
          >
            <FiArrowLeft className="mr-2" /> Back to Admin
          </button>
          <h1 className="text-3xl font-bold text-gray-800">Character Management</h1>
          <p className="text-gray-600 mt-2">Manage predefined characters for the platform</p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            {error}
          </div>
        )}

        {/* Main content */}
        {showForm ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingCharacter ? 'Edit Character' : 'Create New Character'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingCharacter(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <AdminCharacterForm
              character={editingCharacter}
              onSubmit={handleFormSubmit}
              isLoading={formLoading}
              onCancel={() => {
                setShowForm(false);
                setEditingCharacter(null);
              }}
            />
          </div>
        ) : (
          <>
            {/* Create button */}
            <div className="mb-6">
              <button
                onClick={handleCreateNew}
                className="flex items-center bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition"
              >
                <FiPlus className="mr-2" /> Create New Character
              </button>
            </div>

            {/* Character list */}
            {characters.length > 0 ? (
              <AdminCharacterList
                characters={characters}
                onEdit={handleEdit}
                onDelete={(character) => setDeleteConfirm(character)}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <p className="text-gray-500 text-lg">No predefined characters yet.</p>
                <button
                  onClick={handleCreateNew}
                  className="mt-4 text-indigo-600 hover:text-indigo-800 font-semibold"
                >
                  Create the first one
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Delete Character?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.characterId)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default AdminCharacterManagement;
